import { useState } from "react";
import { toast } from "sonner";
import { Save, Plus, ChevronUp, ChevronDown, ChevronRight, Trash2, Camera, Loader2, EyeOff } from "lucide-react";
import { updateService, uploadSiteImage } from "../../lib/api";
import { logger } from "../../lib/logger";
import { DEFAULT_EMPTY_SERVICE, TIP_REMOVE_SERVICE, TIP_REMOVE_IMAGE } from "../../lib/constants";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Switch } from "../ui/switch";

export default function AdminServicesEditor({ readOnly = false, services, setServices, uploading, setUploading, handleSaveService, activeLang }) {
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const normalizedServices = services.map(s => ({
    ...s,
    bullets: Array.isArray(s.bullets) ? s.bullets : typeof s.bullets === "string" ? JSON.parse(s.bullets || "[]") : [],
  }));

  async function handleImageUpload(e, serviceId) {
    if (readOnly) { toast.warning("Sem permissão para editar."); return; }
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(prev => ({ ...prev, services: { ...prev.services, [serviceId]: true } }));
    try {
      const url = await uploadSiteImage(file);
      setServices(services.map(s => s.uid === serviceId ? { ...s, image_url: url } : s));
      toast.success("Imagem do serviço enviada.");
    } catch (err) {
      logger.error(err);
      toast.error("Falha ao enviar imagem.");
    } finally {
      setUploading(prev => ({ ...prev, services: { ...prev.services, [serviceId]: false } }));
    }
  }

  function handleRemoveImage(serviceId) {
    if (readOnly) { toast.warning("Sem permissão para editar."); return; }
    setServices(services.map(s => s.uid === serviceId ? { ...s, image_url: "" } : s));
  }

  async function handleMoveService(index, direction) {
    if (readOnly) { toast.warning("Sem permissão para editar."); return; }
    const newServices = [...services];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newServices.length) return;
    const temp = newServices[index];
    newServices[index] = newServices[targetIndex];
    newServices[targetIndex] = temp;
    setServices(newServices);
    try {
      await Promise.all(newServices.map((s, i) => updateService(s.uid, { order_index: i }, activeLang)));
      toast.success("Ordem atualizada.");
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao salvar nova ordem.");
    }
  }

  const [creating, setCreating] = useState(false);

  async function handleAddService() {
    if (readOnly) { toast.warning("Sem permissão para editar."); return; }
    if (creating) return;
    setCreating(true);
    try {
      const { createService } = await import("../../lib/api");
      const newService = {
        ...DEFAULT_EMPTY_SERVICE,
        icon_name: "Briefcase",
        image_url: "",
        order_index: services.length,
      };
      const created = await createService(newService);
      setServices([...services, created]);
      toast.success("Novo serviço adicionado (Rascunho).");
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao adicionar serviço.");
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteService() {
    if (readOnly) { toast.warning("Sem permissão para editar."); return; }
    if (!serviceToDelete) return;
    try {
      const { deleteService } = await import("../../lib/api");
      await deleteService(serviceToDelete);
      setServices(services.filter(s => s.uid !== serviceToDelete));
      toast.success("Serviço removido.");
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao remover serviço.");
    } finally {
      setServiceToDelete(null);
    }
  }

  async function handleTogglePublish(id, currentStatus) {
    if (readOnly) { toast.warning("Sem permissão para editar."); return; }
    try {
      await updateService(id, { published: !currentStatus }, activeLang);
      setServices(services.map(s => s.uid === id ? { ...s, published: !currentStatus } : s));
      toast.success(currentStatus ? "Serviço movido para rascunho." : "Serviço publicado.");
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao alterar status de publicação.");
    }
  }

  return (
    <div data-id="admin-services" className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-fendi p-4 md:p-6">
        <div>
          <p className="eyebrow">Estrutura de Serviços</p>
          <p className="text-warmgray text-xs mt-1 italic">Gerencie a ordem e o conteúdo das frentes de atuação.</p>
        </div>
        {!readOnly && (
          <button onClick={handleAddService} disabled={creating} className="btn-primary flex items-center justify-center gap-2 !py-2 shadow-sm w-full sm:w-auto disabled:opacity-50">
            <Plus size={16} /> Adicionar Serviço
          </button>
        )}
      </div>

      <div className="grid gap-6">
        {normalizedServices.map((service, idx) => (
          <div key={service.uid} className={`bg-white border ${service.published ? 'border-fendi' : 'border-dashed border-warmgray/60'} group overflow-hidden transition-all hover:shadow-md`}>
            <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 md:px-8 py-3 md:py-4 ${service.published ? 'bg-fendi/5' : 'bg-warmgray/5'} border-b border-fendi/30`}>
              <div className="flex items-center gap-3">
                <span className={`font-serif text-lg md:text-xl ${service.published ? 'text-champagne' : 'text-warmgray/60'}`}>{(idx + 1).toString().padStart(2, '0')}</span>
                <div className="flex flex-col">
                  <h3 className="font-medium text-softblack uppercase tracking-[0.2em] text-[10px]">{service.title || "Novo Serviço"}</h3>
                  {!service.published && <span className="text-[8px] text-warmgray uppercase tracking-widest mt-0.5 flex items-center gap-1"><EyeOff size={8} /> Rascunho</span>}
                </div>
              </div>
              <button onClick={() => setCollapsed(p => ({ ...p, [service.uid]: !p[service.uid] }))} className="p-1 text-warmgray/40 hover:text-softblack transition-colors shrink-0">
                {collapsed[service.uid] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
              </button>
              {!readOnly && (
                <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 bg-white/50 px-2 md:px-3 py-1 border border-fendi/20 rounded-full">
                    <span className="text-[8px] md:text-[9px] uppercase tracking-wider text-warmgray hidden xs:inline">{service.published ? "Publicado" : "Privado"}</span>
                    <Switch checked={service.published} onCheckedChange={() => handleTogglePublish(service.uid, service.published)} className="scale-75" />
                  </div>
                  <div className="flex border border-fendi/40 rounded-sm overflow-hidden bg-white">
                    <button disabled={idx === 0} onClick={() => handleMoveService(idx, -1)} className="p-1.5 md:p-2 hover:bg-fendi/10 disabled:opacity-30 border-r border-fendi/40 transition-colors">
                      <ChevronUp size={12} strokeWidth={1.5} />
                    </button>
                    <button disabled={idx === normalizedServices.length - 1} onClick={() => handleMoveService(idx, 1)} className="p-1.5 md:p-2 hover:bg-fendi/10 disabled:opacity-30 transition-colors">
                      <ChevronDown size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                  <button onClick={() => setServiceToDelete(service.uid)} className="p-1.5 md:p-2 text-destructive hover:bg-destructive/10 rounded-sm transition-colors" title={TIP_REMOVE_SERVICE}>
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>

            {!collapsed[service.uid] && <fieldset disabled={readOnly} className="p-8">
              <div className="grid md:grid-cols-4 gap-8">
                <div className="md:col-span-1">
                  <span className="eyebrow block mb-3 text-[10px]">Imagem de Detalhe</span>
                  <div className="relative group w-full aspect-[4/3] bg-fendi/10 overflow-hidden border border-fendi/30 rounded-sm">
                    {service.image_url ? (
                      <img src={service.image_url} alt="Service" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-warmgray"><Camera size={24} strokeWidth={1} /></div>
                    )}
                    {!readOnly && (
                      <label className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white">
                        {service.image_url && (
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveImage(service.uid); }}
                            className="absolute top-2 right-2 bg-white/10 hover:bg-destructive p-1.5 rounded-full transition-all"
                            title={TIP_REMOVE_IMAGE}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                        {uploading?.services?.[service.uid] ? <Loader2 className="animate-spin" /> : <Camera size={20} />}
                        <input type="file" className="hidden" accept="image/*" onChange={e => handleImageUpload(e, service.uid)} disabled={uploading.services[service.uid]} />
                      </label>
                    )}
                  </div>
                </div>

                <div className="md:col-span-3 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <label className="block">
                      <span className="eyebrow text-[10px]">Título</span>
                      <input className="editorial-field mt-2" value={service?.title ?? ""}
                        onChange={e => setServices(services.map(s => s.uid === service.uid ? {...s, title: e.target.value} : s))} />
                    </label>
                    <label className="block">
                      <span className="eyebrow text-[10px]">Ícone (Lucide Name)</span>
                      <input className="editorial-field mt-2" value={service?.icon_name ?? ""}
                        onChange={e => setServices(services.map(s => s.uid === service.uid ? {...s, icon_name: e.target.value} : s))} />
                    </label>
                  </div>
                  <label className="block">
                    <span className="eyebrow text-[10px]">Descrição Editorial</span>
                    <textarea className="editorial-field mt-2 text-sm" rows={2} value={service?.description ?? ""}
                      onChange={e => setServices(services.map(s => s.uid === service.uid ? {...s, description: e.target.value} : s))} />
                  </label>
                </div>
              </div>

              <div className="mt-8 border-t border-fendi/20 pt-6">
                <span className="eyebrow block mb-4 text-[10px]">Pontos de Destaque (Bullets)</span>
                <div className="grid md:grid-cols-3 gap-4">
                  {service.bullets.map((b, bIdx) => (
                    <div key={bIdx} className="flex gap-2">
                      <input className="editorial-field text-xs !py-2" value={b}
                        onChange={e => {
                          const newBullets = [...service.bullets];
                          newBullets[bIdx] = e.target.value;
                          setServices(services.map(s => s.uid === service.uid ? {...s, bullets: newBullets} : s));
                        }} />
                    </div>
                  ))}
                </div>
              </div>

              {!readOnly && (
              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => handleSaveService(service.uid, {
                    title: service.title,
                    description: service.description,
                    image_url: service.image_url,
                    icon_name: service.icon_name,
                    bullets: service.bullets,
                    published: service.published
                  })}
                  className="btn-primary !text-[10px] !py-2 !px-6"
                >
                  <Save size={12} className="mr-2" /> Guardar Alterações
                </button>
              </div>
            )}
            </fieldset>}
            </div>
        ))}
      </div>

      <AlertDialog open={!!serviceToDelete} onOpenChange={() => setServiceToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi rounded-none">
          <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-3xl text-softblack">Eliminar Serviço?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Esta ação removerá permanentemente o serviço da sua lista e da página principal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Manter Serviço</AlertDialogCancel>
            {!readOnly && (
              <AlertDialogAction onClick={handleDeleteService} className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none">
                Confirmar Eliminação
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
